import type { InvestorIndexFeed } from '@workspace/api-backend'
import {
  certificationAgeDays,
  INVESTOR_INDEX_FEED_SCHEMA,
  isPublishableCertification,
  MAX_CERTIFICATION_AGE_DAYS,
  PUBLISHABLE_CERTIFICATION_STATUSES,
} from '@workspace/seedforge-shared'

export type PreflightCheck = {
  label: string
  ok: boolean
  detail: string
}

export type FeedPreflight = {
  /** The parsed document when it is at least a feed-shaped object. */
  feed: InvestorIndexFeed | null
  checks: PreflightCheck[]
  ok: boolean
  pagesByKind: Record<string, number>
}

const FEED_KINDS = ['list', 'fund', 'index', 'map'] as const

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/**
 * The same gates the backend enforces, run in the browser before the upload
 * so the operator sees why a feed will be refused without a round trip. The
 * server remains the authority; this only mirrors it.
 */
export function preflightFeed(text: string): FeedPreflight {
  let parsed: unknown
  try {
    parsed = JSON.parse(text)
  } catch {
    return {
      feed: null,
      ok: false,
      pagesByKind: {},
      checks: [{ label: 'Valid JSON', ok: false, detail: 'The file is not valid JSON.' }],
    }
  }

  if (!isRecord(parsed)) {
    return {
      feed: null,
      ok: false,
      pagesByKind: {},
      checks: [{ label: 'Feed document', ok: false, detail: 'The file is not a JSON object.' }],
    }
  }

  const checks: PreflightCheck[] = []
  const schema = parsed.schema
  checks.push({
    label: 'Feed schema',
    ok: schema === INVESTOR_INDEX_FEED_SCHEMA,
    detail:
      schema === INVESTOR_INDEX_FEED_SCHEMA
        ? INVESTOR_INDEX_FEED_SCHEMA
        : `Expected ${INVESTOR_INDEX_FEED_SCHEMA}, found ${String(schema ?? 'nothing')}.`,
  })

  const preview = parsed.preview
  checks.push({
    label: 'Certified pool',
    ok: preview === false,
    detail:
      preview === false
        ? 'Not a preview feed.'
        : 'Preview feeds come from an uncertified pool and cannot be published.',
  })

  const certification = isRecord(parsed.certification) ? parsed.certification : {}
  const status = typeof certification.status === 'string' ? certification.status : ''
  checks.push({
    label: 'Certification status',
    ok: isPublishableCertification(status),
    detail: isPublishableCertification(status)
      ? status
      : `${status || 'missing'}; only ${PUBLISHABLE_CERTIFICATION_STATUSES.join(' or ')} publish.`,
  })

  const date = typeof certification.date === 'string' ? certification.date : ''
  const age = certificationAgeDays(date)
  const ageOk = !Number.isNaN(age) && age >= 0 && age <= MAX_CERTIFICATION_AGE_DAYS
  checks.push({
    label: 'Certification age',
    ok: ageOk,
    detail: Number.isNaN(age)
      ? 'No certification date.'
      : age < 0
        ? `${date} is in the future.`
        : `${date}, ${age} of ${MAX_CERTIFICATION_AGE_DAYS} days.`,
  })

  const poolSha = typeof parsed.pool_sha256 === 'string' ? parsed.pool_sha256 : ''
  const shaOk = /^[0-9a-f]{64}$/.test(poolSha)
  checks.push({
    label: 'Pool checksum',
    ok: shaOk,
    detail: shaOk ? poolSha.slice(0, 12) : 'pool_sha256 is missing or malformed.',
  })

  const pages = Array.isArray(parsed.pages) ? parsed.pages : []
  const pagesByKind: Record<string, number> = {}
  const seen = new Set<string>()
  const duplicates = new Set<string>()
  let malformed = 0
  for (const page of pages) {
    if (!isRecord(page) || typeof page.page_key !== 'string' || typeof page.kind !== 'string') {
      malformed++
      continue
    }
    pagesByKind[page.kind] = (pagesByKind[page.kind] ?? 0) + 1
    if (seen.has(page.page_key)) duplicates.add(page.page_key)
    seen.add(page.page_key)
  }
  const unknownKinds = Object.keys(pagesByKind).filter(
    (kind) => !FEED_KINDS.includes(kind as (typeof FEED_KINDS)[number])
  )
  const pagesOk = pages.length > 0 && malformed === 0 && unknownKinds.length === 0
  checks.push({
    label: 'Pages',
    ok: pagesOk,
    detail:
      pages.length === 0
        ? 'The feed carries no pages.'
        : malformed > 0
          ? `${malformed} pages lack page_key or kind.`
          : unknownKinds.length > 0
            ? `Unknown page kind: ${unknownKinds.join(', ')}.`
            : `${pages.length} pages: ${FEED_KINDS.filter((kind) => pagesByKind[kind])
                .map((kind) => `${pagesByKind[kind]} ${kind}`)
                .join(', ')}.`,
  })
  checks.push({
    label: 'Unique page keys',
    ok: duplicates.size === 0,
    detail: duplicates.size === 0 ? 'No duplicates.' : `Repeated: ${[...duplicates].join(', ')}.`,
  })

  const counts = isRecord(parsed.counts) ? parsed.counts : {}
  const countsMatch =
    counts.list_pages === (pagesByKind.list ?? 0) && counts.fund_pages === (pagesByKind.fund ?? 0)
  checks.push({
    label: 'Declared counts',
    ok: countsMatch,
    detail: countsMatch
      ? `${String(counts.list_pages)} list pages, ${String(counts.fund_pages)} fund sheets, ${String(counts.public_rows)} public rows.`
      : 'counts.list_pages or counts.fund_pages disagree with the pages in the file.',
  })

  const ok = checks.every((check) => check.ok)
  return {
    feed: ok ? (parsed as unknown as InvestorIndexFeed) : null,
    checks,
    ok,
    pagesByKind,
  }
}
